import { useState } from "react";

import clsx from "clsx";
import moment from "moment";

import {
  dateRangeToInterval,
  parseDateInterval,
  todayLocalAsISODate,
  type DateInterval,
  type DateRangeChoiceV4,
  type FixedDateInterval,
} from "@north-beam/nb-common";

import { PrimaryButton, SecondaryButton } from "../buttons";
import { Calendar } from "../calendar";

import { QUICK_SELECT_OPTIONS } from "./constants";
import { OptionItem } from "./option-item";

interface Props {
  value: FixedDateInterval | DateRangeChoiceV4 | null;
  minDate?: string;
  maxDate?: string;
  close: () => void;
  onChange: (value: FixedDateInterval | DateRangeChoiceV4 | null) => void;
}

export const DatePickerPanel = ({
  value,
  minDate,
  maxDate,
  close,
  onChange,
}: Props) => {
  const [dateRange, setDateRange] = useState<DateInterval | null>(() => {
    if (!value) {
      return null;
    }

    if (typeof value === "string") {
      return parseDateInterval(
        dateRangeToInterval(value),
        todayLocalAsISODate(),
      );
    }

    return { startDate: value.startDate, endDate: value.endDate };
  });

  return (
    <div className="flex">
      <div className="w-32 p-[1px] border-r border-v2-tertiary bg-v2-primary">
        {QUICK_SELECT_OPTIONS.map((option) => {
          const startDate = option.relativeDate().format();

          return (
            <OptionItem
              key={option.value}
              option={option}
              disabled={minDate ? moment(minDate).isAfter(startDate) : false}
              selected={!dateRange && value === option.value}
              onClick={() => {
                onChange(option.value);
                close();
              }}
            />
          );
        })}
      </div>
      <div
        className={clsx(
          "w-[580px] bg-v2-highlight px-6 py-4 flex flex-col justify-between",
        )}
      >
        <div>
          <Calendar
            minDate={minDate}
            maxDate={maxDate}
            value={dateRange}
            onChange={(val) => setDateRange(val)}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <SecondaryButton size="sm" onClick={() => close()}>
            CANCEL
          </SecondaryButton>
          <PrimaryButton
            size="sm"
            onClick={() => {
              if (dateRange) {
                onChange({ ...dateRange, type: "fixed" });
              }
              close();
            }}
          >
            DONE
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
